import { Box, Button, ButtonGroup, Card, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '../../app/AppStyles';
import { fetchTimetableComplete, postTimetableMoments, resetTimetableMomentManagementStatus } from './timetableManagementSlice';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    width: { xs: '90%', md: '50%' },
    maxHeight: '90%',
    boxShadow: 24,
};

const defaultMoment = {
    timetableId: "",
    remark: "",
    startDay: null,
    endDay: null,
    amount: 0,
    wholeDay: true,
    startTime: null,
    endTime: null,
    daysOfTheWeek: [],
    weekInterval: 1
};

export default function TimetableSelectionAddCard({ handleClose, timetableId, selectedDates }) {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { timetableMomentsPost } = useSelector((state) => state.management.timetable);

    // Status callback
    useEffect(() => {
        if (timetableMomentsPost.status === "success") {
            dispatch(resetTimetableMomentManagementStatus());
            dispatch(fetchTimetableComplete(timetableId));
            handleClose();
        } else if (timetableMomentsPost.status === "failed") {
            dispatch(resetTimetableMomentManagementStatus());
        };
    }, [dispatch, handleClose, timetableMomentsPost.status]);

    // Property states
    const [momentsData, setMomentsData] = useState(defaultMoment);
    const [completeCheck, setCompleteCheck] = useState(true);

    // Keep track of completed state 
    useEffect(() => {
        if (
            (momentsData.startTime !== null || momentsData.wholeDay) &&
            (momentsData.endTime !== null || momentsData.wholeDay)
        ) {
            setCompleteCheck(true);
        } else {
            setCompleteCheck(false);
        }
    }, [momentsData]);

    // Add click function (check property character limits etc.)
    const addClick = () => {
        let tempObject = { ...momentsData }; // Needed to circumvent weird read only property error

        if (!tempObject.wholeDay) {
            tempObject.startTime = tempObject.startTime.format("HH:mm:ss");
            tempObject.endTime = tempObject.endTime.format("HH:mm:ss");
        } else {
            tempObject.startTime = "00:00:00";
            tempObject.endTime = "00:00:00";
        }

        let date = new Date(selectedDates.endStr);
        date.setDate(date.getDate() - 1);
        let year = date.getFullYear();
        let month = String(date.getMonth() + 1).padStart(2, '0');
        let day = String(date.getDate()).padStart(2, '0');
        let formattedDate = `${year}-${month}-${day}`;

        tempObject.timetableId = timetableId;
        tempObject.startDay = selectedDates.startStr;
        tempObject.endDay = formattedDate;
        tempObject.createdBy = `AP/${user.number}`;

        dispatch(postTimetableMoments(tempObject));
    };

    return (
        <Box sx={{ ...modalStyle }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', p: 2 }}>
                <Stack spacing={1} sx={{ flex: '1 1 auto' }}>
                    <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.TIMETABLE}{resourceFinal[appLanguage].words.MOMENT.toLowerCase()} {resourceFinal[appLanguage].words.ADD}</Typography>
                    <TextField
                        fullWidth
                        label={resourceFinal[appLanguage].words.REMARK}
                        onChange={(event) => setMomentsData({
                            ...momentsData,
                            remark: event.target.value
                        })}
                        variant="outlined"
                    />
                    <FormControlLabel
                        label={`${resourceFinal[appLanguage].words.WHOLE} ${resourceFinal[appLanguage].words.DAY.toLowerCase()}`}
                        control={<Checkbox
                            checked={momentsData.wholeDay}
                            onChange={(event) => setMomentsData({
                                ...momentsData,
                                wholeDay: event.target.checked ? true : false
                            })}
                        />}
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                        <TimePicker
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={momentsData.wholeDay}
                            label={`${resourceFinal[appLanguage].words.START}${resourceFinal[appLanguage].words.TIME.toLowerCase()} *`}
                            onChange={(value) => setMomentsData({
                                ...momentsData,
                                startTime: dayjs(value)
                            })}
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                        <TimePicker
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={momentsData.wholeDay}
                            label={`${resourceFinal[appLanguage].words.END}${resourceFinal[appLanguage].words.TIME.toLowerCase()} *`}
                            onChange={(value) => setMomentsData({
                                ...momentsData,
                                endTime: dayjs(value)
                            })}
                        />
                    </LocalizationProvider>
                    {timetableMomentsPost.data === false && <>
                        <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}</Typography>
                    </>}
                    <ButtonGroup sx={{ justifyContent: 'center' }}>
                        <Button variant="outlined" color="error" onClick={handleClose}>{resourceFinal[appLanguage].words.CANCEL}</Button>
                        <Button variant="outlined" color="primary" disabled={!completeCheck} onClick={addClick}>{resourceFinal[appLanguage].words.ADD}</Button>
                    </ButtonGroup>
                </Stack>
            </Card>
        </Box>
    );
}
